// pages/OrientationResultPage.jsx — Résultat du pré-onboarding (spec CTO §6).
// Lit sessionStorage, recalcule la reco déterministe, affiche plan + prix + 3
// raisons max + CTA inscription contextualisée (?role=&plan=). Serveur revalide.
import React, { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OnboardingPage.css";
import { loadOrientation, recommendPlan, buildRegisterUrl, PLAN_META } from "../lib/orientation";

const PLAN_NAME_FR = {
  standard: "Standard",
  premium: "Premium",
  advisor: "Art Advisor",
  advisor_premium: "Art Advisor Premium",
  family_office: "Family Office",
  fondation: "Fondation",
};

const REASON_FR = {
  ai: "Vous utilisez l'OCR et les analyses IA",
  market: "Vous suivez la valeur et le marché",
  succession: "Vous préparez la transmission",
  insurance: "Vous préparez assurance et conservation",
  certification: "Vous certifiez et tracez vos œuvres",
  collaboration: "Plusieurs personnes accèdent à la collection",
  essentials: "Gestion de collection essentielle",
  inventory: "Inventaire et documents centralisés",
  manyClients: "Vous accompagnez plus de 5 clients",
  whiteLabel: "Vous voulez des rapports en marque blanche",
  team: "Vous gérez une équipe",
  unlimited: "Vous avez besoin de clients illimités",
  advisorBasic: "Accompagnement jusqu'à 5 clients",
  upToClients: "Jusqu'à 5 clients suivis",
  foConsolidated: "Vue patrimoniale consolidée",
  foReporting: "Valorisation et reporting",
  foSuccession: "Succession et transmission",
  foundationSmall: "Une collection, équipe réduite",
  foundationMode: "Premium avec mode Fondation",
  foundationSuite: "Suite institutionnelle : prêts, dépôts, expositions",
  foundationComplex: "Plusieurs collections ou grande équipe : configuration sur mesure",
};

const SCENARIO_FR = {
  foStart: "Créez ou invitez une première famille pour démarrer.",
  foCsv: "Nous vous proposons un import structuré depuis Excel / CSV.",
  foPdf: "Nous vous proposons l'OCR et l'import assisté de vos PDF / catalogues.",
  foMigration: "Nous vous proposons une migration depuis votre logiciel actuel.",
  foMeeting: "Un rendez-vous de déploiement est recommandé.",
  foErp: "Un échange technique (ERP) est recommandé.",
};

const page = { minHeight: "100vh", background: "#f9f7f4", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1rem", fontFamily: "Georgia, 'Times New Roman', serif" };
const card = { width: "100%", maxWidth: 560, background: "#fff", border: "1px solid #e5e0d8", borderRadius: 20, padding: "2.5rem" };
const eyebrow = { fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#a9843f", margin: 0 };
const h1 = { fontSize: "2rem", lineHeight: 1.2, color: "#1a1a1a", margin: "0.75rem 0 0.25rem" };
const priceStyle = { fontSize: "1.15rem", color: "#6b6459", margin: "0 0 1.5rem" };
const reasonRow = { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, color: "#3d3a34", fontSize: "0.98rem", lineHeight: 1.5 };
const note = { background: "#faf6ec", border: "1px solid #e6d9b3", borderRadius: 12, padding: "0.85rem 1rem", color: "#7a5f1a", fontSize: "0.9rem", margin: "1.5rem 0 0" };
const primary = { display: "block", textAlign: "center", padding: "0.9rem 1.5rem", borderRadius: 12, background: "#c9a961", color: "#1a1a1a", fontWeight: 600, fontSize: "1rem", textDecoration: "none", marginTop: "1.75rem" };
const secondary = { display: "block", textAlign: "center", padding: "0.8rem 1.5rem", borderRadius: 12, border: "1px solid #e5e0d8", color: "#1a1a1a", fontSize: "0.95rem", textDecoration: "none", marginTop: "0.75rem", background: "#fff" };
const restart = { display: "block", textAlign: "center", marginTop: "1.25rem", color: "#8a8378", fontSize: "0.85rem", textDecoration: "underline" };

function Check() {
  return (
    <span aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, width: 18, height: 18, borderRadius: 999, background: "#c9a961", color: "#1a1a1a", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>✓</span>
  );
}

export default function OrientationResultPage() {
  const { t } = useTranslation();
  const tr = (k, fr) => t(k, { defaultValue: fr });
  const navigate = useNavigate();

  const stored = useMemo(() => loadOrientation(), []);

  useEffect(() => {
    if (!stored?.profile_type) navigate("/orientation", { replace: true });
  }, [stored, navigate]);

  if (!stored?.profile_type) return null;

  const profileType = stored.profile_type;
  const rec = recommendPlan(profileType, stored.answers || {});

  const isContactOnly = !rec.plan && rec.contactRecommended;
  const planName = rec.plan
    ? tr(`orientation.plans.${rec.plan}`, PLAN_NAME_FR[rec.plan])
    : tr("orientation.result.foundationConfig", "Configuration Fondation");

  const price = rec.plan ? PLAN_META[rec.plan]?.price : null;

  return (
    <div style={page}>
      <div style={card}>
        <div className="ob-stagger" key="result">
          <p style={eyebrow}>{tr("orientation.result.eyebrow", "Notre recommandation")}</p>
          <h1 style={h1}>
            {tr("orientation.result.headline", "Nous vous recommandons")} {planName}
          </h1>
          {price ? (
            <p style={priceStyle}>{price} <span style={{ fontSize: "0.9rem" }}>{tr("orientation.result.perMonth", "/ mois")}</span></p>
          ) : (
            <p style={priceStyle}>{tr("orientation.result.onContact", "Sur mesure, sur contact")}</p>
          )}

          <div>
            {(rec.reasons || []).map((r) => (
              <div key={r} style={reasonRow}>
                <Check />
                <span>{tr(`orientation.reasons.${r}`, REASON_FR[r] || r)}</span>
              </div>
            ))}
          </div>

          {rec.migrationRecommended && (
            <p style={note}>{tr("orientation.result.migration", "Vous avez un inventaire existant : nous vous aiderons à l'importer (Excel, PDF, catalogues).")}</p>
          )}
          {rec.scenario && SCENARIO_FR[rec.scenario] && (
            <p style={note}>{tr(`orientation.result.scenario.${rec.scenario}`, SCENARIO_FR[rec.scenario])}</p>
          )}

          {isContactOnly ? (
            <a href="/contact?from=orientation" style={primary}>
              {tr("orientation.result.talk", "Parler à ARTKIV")}
            </a>
          ) : (
            <a href={buildRegisterUrl(profileType, rec)} style={primary}>
              {tr("orientation.result.createAccount", "Créer mon compte")}
            </a>
          )}

          {!isContactOnly && rec.alt && (
            <Link to="/plans/compare-plans" style={secondary}>
              {tr("orientation.result.compare", "Comparer avec")} {tr(`orientation.plans.${rec.alt}`, PLAN_NAME_FR[rec.alt])}
            </Link>
          )}
          {!isContactOnly && rec.contactRecommended && (
            <a href="/contact?from=orientation" style={secondary}>
              {tr("orientation.result.talk", "Parler à ARTKIV")}
            </a>
          )}

          <Link to="/orientation" style={restart}>{tr("orientation.result.restart", "Recommencer le questionnaire")}</Link>
        </div>
      </div>
    </div>
  );
}
